import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, getDocs, addDoc,
  serverTimestamp, query, orderBy
} from 'firebase/firestore';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart,
  Pie, Cell, Legend
} from 'recharts';
import Sidebar from '../Shared/Sidebar';
import { useAuth } from '../../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';

export default function MonthlyReport() {
  const { currentUser } = useAuth();
  const [reports, setReports] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    month: '',
    year: new Date().getFullYear().toString(),
    highlights: '',
    challenges: '',
    nextSteps: '',
    studentsAdded: '',
    parentsAdded: '',
    sessionsHeld: '',
    totalDonations: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const studentsSnap = await getDocs(
        collection(db, 'students')
      );
      const parentsSnap = await getDocs(
        collection(db, 'parents')
      );
      const sessionsSnap = await getDocs(
        collection(db, 'sessions')
      );
      const reportsSnap = await getDocs(
        query(
          collection(db, 'monthlyReports'),
          orderBy('createdAt', 'desc')
        )
      );
      setStudents(
        studentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setParents(
        parentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setSessions(
        sessionsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setReports(
        reportsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error(err);
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const reportId = `IYA-RPT-${uuidv4().slice(0, 8).toUpperCase()}`;
      await addDoc(collection(db, 'monthlyReports'), {
        ...form,
        reportId,
        totalStudents: students.length,
        totalParents: parents.length,
        totalSessions: sessions.length,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });
      setSuccess('Monthly report published successfully!');
      setShowForm(false);
      fetchData();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  // Chart data
  const pieData = [
    { name: 'Students', value: students.length, color: '#14452F' },
    { name: 'Parents', value: parents.length, color: '#F57C00' },
    { name: 'Sessions', value: sessions.length, color: '#4527A0' },
  ];

  const certData = [
    {
      name: 'Has Certificate',
      value: students.filter(s =>
        s.hasBirthCertificate === 'yes'
      ).length,
      color: '#14452F'
    },
    {
      name: 'Needs Certificate',
      value: students.filter(s =>
        s.hasBirthCertificate === 'no'
      ).length,
      color: '#C62828'
    },
    {
      name: 'In Process',
      value: students.filter(s =>
        s.hasBirthCertificate === 'processing'
      ).length,
      color: '#F57C00'
    },
  ];

  const attendanceData = sessions.slice(0, 6).map(s => ({
    date: s.date,
    present: s.attendance?.totalPresent || 0,
    absent: s.attendance?.totalAbsent || 0,
  }));

  const months = [
    'January', 'February', 'March', 'April',
    'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'
  ];

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Monthly Reports
            </div>
            <div style={styles.pageSub}>
              Generate and publish reports for donors
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : '+ New Report'}
          </button>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}

        {/* LIVE STATS */}
        <div style={styles.statsGrid}>
          {[
            { emoji: '', value: students.length, label: 'Total Students', color: '#14452F' },
            { emoji: '', value: parents.length, label: 'Total Parents', color: '#F57C00' },
            { emoji: '', value: sessions.length, label: 'Total Sessions', color: '#4527A0' },
            {
              emoji: '',
              value: students.filter(s =>
                s.hasBirthCertificate === 'no'
              ).length,
              label: 'Need Certificate',
              color: '#C62828'
            },
          ].map((s, i) => (
            <div key={i} style={styles.statCard}>
              <div style={{
                ...styles.statBar,
                background: `linear-gradient(90deg, ${s.color}, ${s.color}99)`
              }} />
              <div style={styles.statEmoji}>{s.emoji}</div>
              <div style={{
                ...styles.statVal,
                background: `linear-gradient(135deg, ${s.color}, ${s.color}99)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                {s.value}
              </div>
              <div style={styles.statLbl}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* CHARTS ROW */}
        <div style={styles.chartsRow}>
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Program Overview
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) =>
                    `${name}: ${value}`
                  }
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.color}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Birth Certificates
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={certData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) =>
                    `${name}: ${value}`
                  }
                >
                  {certData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.color}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ATTENDANCE CHART */}
        {attendanceData.length > 0 && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Recent Session Attendance
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="present"
                  fill="#14452F"
                  name="Present"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="absent"
                  fill="#C62828"
                  name="Absent"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* CREATE REPORT FORM */}
        {showForm && (
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Create Monthly Report
            </div>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Month *
                  </label>
                  <select
                    style={styles.formInput}
                    name="month"
                    value={form.month}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select month</option>
                    {months.map(m => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Year *
                  </label>
                  <input
                    style={styles.formInput}
                    name="year"
                    value={form.year}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    New Students This Month
                  </label>
                  <input
                    style={styles.formInput}
                    name="studentsAdded"
                    type="number"
                    value={form.studentsAdded}
                    onChange={handleChange}
                    placeholder="e.g. 5"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    New Parents This Month
                  </label>
                  <input
                    style={styles.formInput}
                    name="parentsAdded"
                    type="number"
                    value={form.parentsAdded}
                    onChange={handleChange}
                    placeholder="e.g. 3"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Sessions Held This Month
                  </label>
                  <input
                    style={styles.formInput}
                    name="sessionsHeld"
                    type="number"
                    value={form.sessionsHeld}
                    onChange={handleChange}
                    placeholder="e.g. 4"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>
                    Total Donations (TSh)
                  </label>
                  <input
                    style={styles.formInput}
                    name="totalDonations"
                    type="number"
                    value={form.totalDonations}
                    onChange={handleChange}
                    placeholder="e.g. 500000"
                  />
                </div>
                <div style={{
                  ...styles.formGroup,
                  gridColumn: '1 / -1'
                }}>
                  <label style={styles.formLabel}>
                    Monthly Highlights *
                  </label>
                  <textarea
                    style={{
                      ...styles.formInput,
                      height: '90px',
                      resize: 'vertical'
                    }}
                    name="highlights"
                    value={form.highlights}
                    onChange={handleChange}
                    placeholder="What went well this month..."
                    required
                  />
                </div>
                <div style={{
                  ...styles.formGroup,
                  gridColumn: '1 / -1'
                }}>
                  <label style={styles.formLabel}>
                    Challenges
                  </label>
                  <textarea
                    style={{
                      ...styles.formInput,
                      height: '80px',
                      resize: 'vertical'
                    }}
                    name="challenges"
                    value={form.challenges}
                    onChange={handleChange}
                    placeholder="Any challenges faced..."
                  />
                </div>
                <div style={{
                  ...styles.formGroup,
                  gridColumn: '1 / -1'
                }}>
                  <label style={styles.formLabel}>
                    Next Steps
                  </label>
                  <textarea
                    style={{
                      ...styles.formInput,
                      height: '80px',
                      resize: 'vertical'
                    }}
                    name="nextSteps"
                    value={form.nextSteps}
                    onChange={handleChange}
                    placeholder="Plans for next month..."
                  />
                </div>
              </div>
              <button
                type="submit"
                style={styles.submitBtn}
                disabled={loading}
              >
                {loading
                  ? 'Publishing...'
                  : 'Publish Report to Donors'}
              </button>
            </form>
          </div>
        )}

        {/* PUBLISHED REPORTS */}
        <div style={styles.panel}>
          <div style={styles.sectionTitle}>
            Published Reports ({reports.length})
          </div>
          {reports.length === 0 ? (
            <div style={styles.empty}>
              No reports published yet.
            </div>
          ) : (
            reports.map(r => (
              <div key={r.id} style={styles.reportRow}>
                <div style={styles.reportIcon}></div>
                <div style={styles.reportInfo}>
                  <div style={styles.reportTitle}>
                    {r.month} {r.year} Report
                  </div>
                  <div style={styles.reportMeta}>
                    {r.totalStudents} students ·
                    {r.totalParents} parents ·
                    {r.sessionsHeld} sessions
                  </div>
                  <div style={styles.reportHighlight}>
                    {r.highlights?.slice(0, 100)}...
                  </div>
                </div>
                <span style={styles.reportBadge}>
                  Published
                </span>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(245,124,0,0.25)',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '14px',
    marginBottom: '22px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px 18px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    position: 'relative',
    overflow: 'hidden',
  },
  statBar: {
    height: '3px',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    borderRadius: '3px 3px 0 0',
  },
  statEmoji: { fontSize: '24px', marginBottom: '10px' },
  statVal: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '30px',
    lineHeight: 1,
    marginBottom: '3px',
  },
  statLbl: { fontSize: '12px', color: '#6B7280' },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '20px',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
    marginBottom: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  formLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  formInput: {
    padding: '11px 14px',
    borderRadius: '9px',
    border: '1.5px solid #E5E7EB',
    background: '#FAF9F5',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  submitBtn: {
    width: '100%',
    padding: '13px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(20,69,47,0.25)',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
  reportRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #F3F4F6',
  },
  reportIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    flexShrink: 0,
  },
  reportInfo: { flex: 1 },
  reportTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '3px',
  },
  reportMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
    marginBottom: '4px',
  },
  reportHighlight: {
    fontSize: '12px',
    color: '#374151',
    lineHeight: 1.5,
  },
  reportBadge: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    background: '#EAF3EC',
    color: '#14452F',
    fontWeight: '500',
    flexShrink: 0,
  },
};